import { MPInstancesHub } from "@/services/api/RequestAxios/RequestAxiosHub";
import { unpackServerLogin, unpackSignUp } from "./auth.mapper";
import type * as types from "./auth.types";
import { stringifyQueryParams } from "../utils";

import { UIModelLogin } from "./auth.model";
import MPStorage from "@/services/LocalStorage";
import { customKeys } from "@/services/LocalStorage/LocalStorage";
import config from "@/config";
import { MPInstanceAuthX360 } from "../RequestAxios/RequestAxiosAuthX360";

const environment = process.env.NEXT_PUBLIC_APP_ENV || "localhost:3000";

const fetchSocialLoginUrl = () => `/token_from_facebook`;
const fetchResetPasswordUrl = () => "/dbconnections/change_password";
const authorizeSocialUrl = () => "/authorize";
const embeddedSignUp = ({ partnerId }: { partnerId: string }) =>
  `/public/partners/${partnerId}/register_client`;
const retrieveOtpInfoUrl = () => `/exports/user_otp_status`;

const authX360LoginUrlWithOrgName = (orgName: string) =>
  `${config.authX360UrlV1}/login?app_name=${config.appName}&organization=${orgName}&env=${environment}`;
const authX360LoginUrl = ({
  prompt,
  flow,
  email,
}: {
  prompt: boolean;
  flow?: string;
  email?: string;
}) =>
  `${config.authX360UrlV1}/login?app_name=${config.appName}&env=${environment}${
    prompt ? `&prompt=${prompt}` : ""
  }${flow ? `&flow=${flow}` : ""}${email ? `&login_hint=${email}` : ""}`;
const authX360LogoutUrl = () =>
  `${config.authX360UrlV1}/logout?app_name=${config.appName}&env=${environment}`;

const tokenUrl = "/token";

export const retrieveOtpInfo = async () => {
  const { data } =
    await MPInstancesHub.buffedInstances.newCrm.get<types.IServerResponseOtpStatus>(
      retrieveOtpInfoUrl()
    );
  return data;
};

export const fetchSocialLogin = async (
  payload: types.SocialLoginPayload
): Promise<UIModelLogin> => {
  const { data } =
    await MPInstancesHub.buffedInstances.newCrm.post<types.ServerResponseLoginBlob>(
      fetchSocialLoginUrl(),
      payload
    );
  return new UIModelLogin(unpackServerLogin(data));
};

export const fetchResetPassword = async (payload: types.resetPassword) => {
  const { data } = await MPInstancesHub.buffedInstances.auth0Api.post(
    fetchResetPasswordUrl(),
    payload
  );
  return data;
};

export const authorizeSocial = async (payload: types.authorizeSocial) => {
  const response = await MPInstancesHub.buffedInstances.auth0Api.get(
    authorizeSocialUrl(),
    {
      params: payload,
      paramsSerializer: stringifyQueryParams,
      // maxRedirects: 0
    }
  );
  // console.log(response.request.responseURL)
  return response;
};

export const signUp = async ({
  partnerId,
  payload,
}: {
  payload: types.IUISignUpRequest;
  partnerId: string;
}) => {
  const { data } = await MPInstancesHub.buffedInstances.newCrm.post(
    embeddedSignUp({ partnerId }),
    payload
  );

  return unpackSignUp(data);
};

export const socialSignUp = async ({
  partnerId,
  payload,
}: {
  payload: types.IUISocialSignUpRequest;
  partnerId: string;
}) => {
  const { data } = await MPInstancesHub.buffedInstances.newCrm.post(
    embeddedSignUp({ partnerId }),
    { ...payload }
  );

  return unpackSignUp(data);
};

export const getCannySSOToken = async (): Promise<{ token: string }> => {
  const { data } = await MPInstancesHub.buffedInstances.newCrm.get<{
    token: string;
  }>("/canny/sso_token");

  return data;
};

export function loginViaAuth360(
  query: { prompt: boolean; flow?: string; email?: string } = { prompt: false },
  orgName?: string,
  automaticRedirect = true
) {
  MPStorage.clearItemLocalStorage(customKeys.AUTH360_ERROR);
  const url = !orgName
    ? authX360LoginUrl(query)
    : authX360LoginUrlWithOrgName(orgName);

  if (automaticRedirect) {
    window.location.href = url;
  }

  return url;
}

export function logoutViaAuth360() {
  window.location.href = authX360LogoutUrl();
}

export const getToken = async () => {
  const response = await MPInstanceAuthX360.buffedInstances.head(tokenUrl, {
    withCredentials: true,
  });
  const token = response.headers["x-authx360-access-token"];
  return token;
};
